// hooks
import { useState } from 'react';

// utils
import { savePreference } from 'RestAPIs/preferences';
import { showErrorSnackbar } from 'components/shared/Snackbar';

// types
import type { Weekday } from './useRegisterAbsenceDialog';

/** fixed working days per work agreement, until the contract carries them.
 *  Deliberately days only: the week hours of a zero-hours contract differ per
 *  sickness period, so remembering them would quietly book the wrong number. */
export type WorkingDaysSetting = {
  days: Weekday[];
};

export type WorkingDaysSettings = Record<number, WorkingDaysSetting>;

const PREFERENCE_KEY = 'absenceWorkingDays';
const SESSION_KEY = 'userPreferences';

type StoredPreference = { key: string; value: string };

const readPreferences = (): StoredPreference[] => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY) || '[]');
  } catch {
    return [];
  }
};

const readAll = (): WorkingDaysSettings => {
  try {
    const raw = readPreferences().find(
      (preference) => preference.key === PREFERENCE_KEY
    )?.value;
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

/**
 * Reads and writes the fixed working days the planner recorded per work
 * agreement. Stored as a user preference (key/value), and mirrored into the
 * session cache so a freshly saved setting is available immediately.
 */
export const useWorkingDaysSettings = () => {
  'use memo';

  // React state next to the session cache: a successful save must re-render
  // the caller, so "days changed" indicators disappear at once
  const [settings, setSettings] = useState<WorkingDaysSettings>(readAll);

  const getSettings = () => settings;

  const saveSettings = async (patch: WorkingDaysSettings) => {
    const merged = { ...readAll(), ...patch };
    const value = JSON.stringify(merged);

    try {
      await savePreference([{ key: PREFERENCE_KEY, value }]);
      const preferences = readPreferences().filter(
        (preference) => preference.key !== PREFERENCE_KEY
      );
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify([...preferences, { key: PREFERENCE_KEY, value }])
      );
      setSettings(merged);
      return true;
    } catch {
      showErrorSnackbar();
      return false;
    }
  };

  return { getSettings, saveSettings };
};
